import { useMemo, useState } from "react";
import { NavLink as NavLinkBase } from "react-router-dom";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import styled from "@mui/material/styles/styled";

import { format } from "date-fns";

import { useAppSelector } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import { selectCurrentUser } from "features/auth/auth-slice";
import { StaffTask } from "services/types/staff-tasks.type";
import { convertHtmlToText, formatBackendDateOnly } from "utils/common";

import Comments from "../../../patient-staff-tasks-drawer/comments.component";

const NavLink = styled(NavLinkBase)(() => ({
  "&": {
    color: misc.grayAsphalt
  },
  "&:hover": {
    cursor: "pointer",
    color: misc.linkColor
  },

  "&.active": {}
}));

type TaskPreviewProps = {
  task: StaffTask;
  showPatientName?: boolean;
  onDelete?: (task: StaffTask) => void;
  onEdit?: (task: StaffTask) => void;
  onComplete?: (task: StaffTask) => void;
  hideComments?: boolean;
};

export default function TaskPreview({
  task,
  showPatientName = false,
  onEdit,
  onDelete,
  onComplete,
  hideComments = false
}: TaskPreviewProps) {
  const currentUser = useAppSelector(selectCurrentUser);
  const {
    title,
    description,
    created_by,
    created_at,
    completed_at,
    due_at,
    assignee,
    assignees,
    patient
  } = task;
  const margin = { mr: 1.5 };
  const sortedAssignees = useMemo(() => {
    if (!assignees || assignees.length === 0) {
      return [];
    }
    if (!currentUser) {
      return assignees;
    }
    const currentUserIndex = assignees.findIndex(
      (assignee) => assignee.user.id === currentUser.id
    );
    if (currentUserIndex !== -1) {
      const assigneesCopy = assignees.slice();
      const [currentUserAssignee] = assigneesCopy.splice(currentUserIndex, 1);
      if (currentUser.id === assignee.id) {
        return assigneesCopy;
      }
      return [currentUserAssignee, ...assigneesCopy];
    }
    return assignees;
  }, [assignees, currentUser, assignee]);

  const [checked, setChecked] = useState(Boolean(completed_at));
  const plainDescription = useMemo(
    () => convertHtmlToText(description),
    [description]
  );

  return (
    <Stack
      className="task_preview"
      sx={{
        borderBottom: `dashed 1px ${misc.darkGreen}`,
        px: 0.5,
        py: 2.5,
        ml: -2
      }}
    >
      <Stack direction="row" alignItems="center">
        <Stack sx={{ width: "44px" }}>
          <Box sx={{ ...margin }}>
            <Checkbox
              checked={checked}
              onChange={(event) => {
                if (onComplete) {
                  setChecked(event.target.checked);
                  onComplete({
                    ...task,
                    completed_at: event.target.checked
                      ? new Date().toISOString()
                      : null
                  });
                }
              }}
            />
          </Box>
        </Stack>

        <Stack width="100%">
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            width="100%"
            height="100%"
          >
            <Box sx={{ width: "100%" }}>
              {Boolean(assignee.fullName) && (
                <Typography
                  variant="body1"
                  sx={{
                    color: misc.grayAsphalt,
                    fontWeight: 400,
                    lineHeight: 1.4
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      color: neutral[700]
                    }}
                  >
                    To:
                  </Box>{" "}
                  <Box
                    component="span"
                    sx={{
                      textDecoration:
                        assignee && !assignee.is_active
                          ? "line-through"
                          : undefined
                    }}
                  >
                    {assignee.fullName}
                  </Box>
                </Typography>
              )}
              {sortedAssignees.length > 0 && (
                <Tooltip
                  title={
                    sortedAssignees.length > 1
                      ? sortedAssignees.map((a) => a.user.fullName).join(", ")
                      : ""
                  }
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: misc.grayAsphalt,
                      fontWeight: 400,
                      lineHeight: 1.4
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 400,
                        fontSize: "12px",
                        color: neutral[700]
                      }}
                    >
                      Additional Assignees:
                    </Box>{" "}
                    <Box component="span">
                      {sortedAssignees[0].user.fullName}
                      {sortedAssignees.length > 1 &&
                        `(+${sortedAssignees.length - 1} more)`}
                    </Box>
                  </Typography>
                </Tooltip>
              )}
              {Boolean(showPatientName && patient?.fullName) && (
                <Typography
                  variant="body1"
                  sx={{
                    color: misc.grayAsphalt,
                    fontWeight: 400,
                    lineHeight: 1.4
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      color: neutral[700]
                    }}
                  >
                    Patient:
                  </Box>{" "}
                  <NavLink to={`/patients/${patient.id}/tasks`}>
                    {patient.fullName}
                  </NavLink>
                </Typography>
              )}
              {patient?.id && (
                <NavLink to={`/patients/${patient.id}/tasks/${task.id}`}>
                  <Typography
                    variant="body2"
                    fontWeight={Boolean(completed_at) ? 400 : 800}
                    sx={{
                      color: "#087069",
                      mt: 0.75
                    }}
                  >
                    {title}
                  </Typography>
                </NavLink>
              )}
              {!patient?.id && (
                <Typography
                  variant="body2"
                  fontWeight={Boolean(completed_at) ? 400 : 800}
                  sx={{
                    color: "#087069",
                    mt: 0.75
                  }}
                >
                  {title}
                </Typography>
              )}

              {Boolean(due_at) && (
                <Typography
                  variant="body1"
                  sx={{
                    color: misc.grayAsphalt,
                    fontWeight: 700
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: "12px",
                      color: neutral[700]
                    }}
                  >
                    Due Date:
                  </Box>{" "}
                  {formatBackendDateOnly(String(due_at), "MMM dd, yyyy")}
                </Typography>
              )}
              {Boolean(plainDescription) && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, whiteSpace: "pre-line" }}
                >
                  {plainDescription}
                </Typography>
              )}
            </Box>

            <Stack
              alignItems="flex-start"
              sx={{
                width: 150,
                ...margin
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  width: "100%",
                  color: misc.blackAsphalt,
                  fontSize: "12px",
                  lineHeight: 1.2,
                  textAlign: "right",
                  mb: 0.5
                }}
              >
                {created_by?.fullName ? `by ${created_by.fullName}` : ""}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: neutral[700],
                  fontSize: "9px",
                  transform: "scale(1.05)",
                  width: "100%",
                  lineHeight: 1.3,
                  textAlign: "right"
                }}
              >
                {format(new Date(created_at), "MMM dd, yyyy hh:mm aaa")}
              </Typography>
            </Stack>

            <Stack alignItems="flex-end">
              {onDelete && (
                <IconButton
                  size="small"
                  sx={{ mt: -1, opacity: 0.7 }}
                  onClick={() => onDelete(task)}
                >
                  <DeleteIcon sx={{ fontSize: "20px" }} />
                </IconButton>
              )}
              {onEdit && (
                <IconButton
                  sx={{ opacity: 0.7 }}
                  size="small"
                  onClick={() => onEdit(task)}
                >
                  <EditIcon sx={{ fontSize: "20px" }} />
                </IconButton>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {!hideComments && (
        <Comments taskId={task.id} comments={task.comments ?? []} />
      )}
    </Stack>
  );
}
